import { MapPin, X } from "lucide-react";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { CityCombobox } from "@/components/forms/CityCombobox";
import { parseOfficeString } from "@/lib/orgOffices";

// Copied verbatim from BrandBasicsSection (serviceTags) and KeyMetricsSection
// (marketsServed) so the wizard writes the exact values those editors and the
// public profile use.
// Canonical list — shared with the profile editor and the website-import
// extractor. Previously declared here verbatim; the copies happened to agree.
import { SERVICE_TAG_OPTIONS, cultureTags } from "@/lib/orgProfileOptions";

const MARKET_OPTIONS = [
  "Residential", "Luxury", "Commercial", "Investment",
  "New Construction", "Land", "Multi-family", "REO/Foreclosure",
];

interface Step5Props {
  data: {
    serviceTags: string[];
    marketsServed: string[];
    locations: string[];
  };
  onUpdate: (data: any) => void;
}

const OrgOnboardingStep5 = ({ data, onUpdate }: Step5Props) => {
  const toggle = (key: "serviceTags" | "marketsServed", value: string) => {
    const current = data[key] || [];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    onUpdate({ ...data, [key]: next });
  };

  // Older non-culture values are dropped the first time the admin changes
  // this field (spec 2026-10-28 §Phase 1).
  const selectedCulture = cultureTags(data.serviceTags);
  const toggleCulture = (tag: string) => {
    const next = selectedCulture.includes(tag)
      ? selectedCulture.filter((t) => t !== tag)
      : cultureTags([...selectedCulture, tag]);
    onUpdate({ ...data, serviceTags: next });
  };

  const filledLocations = (data.locations || []).filter((l) => l.trim() !== "");

  const addLocation = (val: string) => {
    if (!val.trim() || filledLocations.includes(val)) return;
    // ", CA" and similar parse to a blank city, which the save path silently
    // drops — that can reduce the payload to [] and delete every office while
    // a chip still shows one added. Reject at input instead.
    if (parseOfficeString(val).city === "") return;
    onUpdate({ ...data, locations: [...filledLocations, val] });
  };

  const removeLocation = (loc: string) => {
    onUpdate({ ...data, locations: filledLocations.filter((l) => l !== loc) });
  };

  return (
    <div className="space-y-6">
      {/* Culture (stored as serviceTags) */}
      <div className="space-y-2">
        <Label>Culture</Label>
        <p className="text-xs text-muted-foreground">What's it like to work here? Pick what fits.</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {SERVICE_TAG_OPTIONS.map((tag) => {
            const selected = selectedCulture.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleCulture(tag)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                )}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      {/* Markets served */}
      <div className="space-y-2">
        <Label>Markets served</Label>
        <p className="text-xs text-muted-foreground">What property segments do you focus on?</p>
        <div className="flex flex-wrap gap-2 pt-1">
          {MARKET_OPTIONS.map((market) => {
            const selected = (data.marketsServed || []).includes(market);
            return (
              <button
                key={market}
                type="button"
                onClick={() => toggle("marketsServed", market)}
                className={cn(
                  "px-3 py-1.5 rounded-full text-xs font-medium border transition-all",
                  selected
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                )}
              >
                {market}
              </button>
            );
          })}
        </div>
      </div>

      {/* Office locations / areas served */}
      <div className="space-y-2">
        <Label className="flex items-center gap-1.5">
          <MapPin className="h-3.5 w-3.5" />
          Areas served
        </Label>
        <div className="flex flex-wrap items-center gap-2">
          {filledLocations.map((loc) => (
            <span
              key={loc}
              className="inline-flex items-center gap-1.5 bg-primary/10 text-primary text-xs font-medium px-3 py-1.5 rounded-full"
            >
              <MapPin className="h-3 w-3" />
              {loc}
              <button
                type="button"
                onClick={() => removeLocation(loc)}
                className="ml-0.5 rounded-full hover:bg-primary/20 p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <div className="min-w-[220px] flex-1 max-w-[300px]">
            <CityCombobox value="" onChange={addLocation} placeholder="Add a city..." />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgOnboardingStep5;
